'use client';

import { Box, Button, CircularProgress } from '@mui/material';
import { useMutation, useQuery } from '@parkchirp/api-client';
import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';

import { UPDATE_LEASE_DETAILS } from '../../../mutations';
import { GET_BUILDINGS_BY_FACILITY_ID } from '../../../queries';
import { SetShowDialog, SetShowSnackbar } from '../../../types';
import { LeasingRight } from '../../../types/leases';
import {
	emptyStringToNull,
	leasingRightErrorMessage,
	validateLeaseRequiredFields,
	validateLeasingRightDates,
} from '../../../utils';
import { isOwnerPortal, isTenantPortal } from '../../../utils/constants';
import { SimpleModal } from '../../modals';
import { LeasingRightFormInputs } from './leasingRightFormInputs';

type LeaseGeneralProps = {
	leasingRight: LeasingRight;
	setLeasingRight: Dispatch<SetStateAction<LeasingRight>>;
	refetchLeasingRight: () => void;
	resetLeasingRight: () => void;
	setShowDialog: SetShowDialog;
	setShowSnackbar: SetShowSnackbar;
	isLeaseGeneralEditing: boolean;
	setIsLeaseGeneralEditing: Dispatch<SetStateAction<boolean>>;
	newMode: boolean;
	isLeaseDetailEditInProgress: boolean;
};

export const LeaseGeneral: FC<LeaseGeneralProps> = ({
	leasingRight,
	setLeasingRight,
	refetchLeasingRight,
	resetLeasingRight,
	setShowDialog,
	setShowSnackbar,
	setIsLeaseGeneralEditing,
	isLeaseGeneralEditing,
	newMode,
	isLeaseDetailEditInProgress,
}) => {
	const [leaseDatesChanged, setLeaseDatesChanged] = useState(false);
	const [saveInProgress, setSaveInProgress] = useState(false);

	const { data: buildingsData, loading: buildingsLoading } = useQuery(GET_BUILDINGS_BY_FACILITY_ID, {
		variables: { facilityId: leasingRight.facilityId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		skip: !leasingRight.facilityId,
		notifyOnNetworkStatusChange: true,
	});
	const buildingsOptions = buildingsData?.getBuildingsByFacilityId ?? [];

	const [updateLeaseDetails] = useMutation(UPDATE_LEASE_DETAILS);

	useEffect(() => {
		if (!newMode) setIsLeaseGeneralEditing(false);
	}, [newMode, setIsLeaseGeneralEditing]);

	const exitEditMode = useCallback(() => {
		setLeaseDatesChanged(false);
		setIsLeaseGeneralEditing(false);
	}, [setIsLeaseGeneralEditing]);

	const handleCancel = useCallback(() => {
		resetLeasingRight();
		exitEditMode();
	}, [exitEditMode, resetLeasingRight]);

	const handleChange = useCallback(
		({ target: { name, value } }) => {
			let val = value;

			const dateFields = ['commencementDate', 'expirationDate'];
			if (dateFields.includes(name)) {
				setLeaseDatesChanged(true);
				val = value.format('YYYY-MM-DD');
			}

			// MUI number input does not work in FireFox so we'll need to manually convert these to numbers
			const numberFields = ['squareFeet'];
			if (numberFields.includes(name)) {
				val = value !== '' ? parseInt(value, 10) : null;
			}

			setLeasingRight({ ...leasingRight, [name]: val });
		},
		[leasingRight, setLeasingRight],
	);

	const handleSave = useCallback(
		async (saveConfirmed = false) => {
			try {
				validateLeaseRequiredFields(leasingRight);

				const invalidDates = validateLeasingRightDates(
					leasingRight.commencementDate,
					leasingRight.expirationDate,
				);
				if (invalidDates) throw new Error(invalidDates);

				if (leaseDatesChanged && !saveConfirmed) {
					setShowDialog(
						<SimpleModal
							content={
								<>
									<span>
										<strong>Warning: </strong>
										Changing the commencement and/or expiration date will auto-adjust the dates for
										count & rates and conversion rights - this may include deletion if the dates are
										no longer within the leasing right.
									</span>
									<br />
									<span>Are you sure you want to proceed?</span>
								</>
							}
							handleDismiss={() => setShowDialog(null)}
							handleConfirm={async () => {
								setShowDialog(null);
								await handleSave(true);
							}}
							confirmText='Confirm'
							dismissText='Cancel'
						/>,
					);
					return;
				}

				setSaveInProgress(true);

				try {
					await updateLeaseDetails({
						variables: emptyStringToNull({
							...leasingRight,
							buildings: leasingRight.buildings.map(({ id }) => id),
						}),
						context: {
							apiVersion: 'v2',
							appName: process.env.NEXT_PUBLIC_APP_NAME,
						},
					});
				} catch (e) {
					console.log(e);
					throw new Error(leasingRightErrorMessage(e));
				}

				setSaveInProgress(false);
				setShowSnackbar({
					message: 'Saved successfully',
					severity: 'success',
					autoHideDuration: 5000,
				});

				exitEditMode();
				await refetchLeasingRight();
			} catch (e) {
				setSaveInProgress(false);
				setShowSnackbar({
					message: e.message,
					severity: 'error',
					autoHideDuration: 8000,
				});
			}
		},
		[
			exitEditMode,
			leaseDatesChanged,
			leasingRight,
			refetchLeasingRight,
			setShowDialog,
			setShowSnackbar,
			updateLeaseDetails,
		],
	);

	const renderButtons = useCallback(() => {
		if (newMode || isTenantPortal || isOwnerPortal) return null;

		return (
			<Box display='flex' justifyContent='end' gap='1em' mt='0.5em'>
				{!isLeaseDetailEditInProgress && (
					<Button variant='contained' onClick={() => setIsLeaseGeneralEditing(true)}>
						Edit
					</Button>
				)}
				{isLeaseGeneralEditing && (
					<>
						<Button variant='contained' disabled={saveInProgress} onClick={handleCancel}>
							Cancel
						</Button>
						{saveInProgress ? (
							<CircularProgress />
						) : (
							<Button variant='contained' style={{ minWidth: '6em' }} onClick={() => handleSave()}>
								Save
							</Button>
						)}
					</>
				)}
			</Box>
		);
	}, [
		handleCancel,
		handleSave,
		newMode,
		saveInProgress,
		isLeaseDetailEditInProgress,
		isLeaseGeneralEditing,
		setIsLeaseGeneralEditing,
	]);

	if (buildingsLoading) {
		return <CircularProgress size={50} sx={{ position: 'absolute', top: '25%', left: '50%' }} />;
	}

	return (
		<Box display='flex' flexDirection='column'>
			{renderButtons()}
			<Box
				sx={{
					'.MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'black' },
					'.MuiInputLabel-root': { WebkitTextFillColor: 'black' },
				}}
			>
				<LeasingRightFormInputs
					formData={leasingRight}
					buildings={buildingsOptions}
					editMode={isLeaseGeneralEditing}
					handleInputChangeLeasingRight={handleChange}
				/>
			</Box>
		</Box>
	);
};
